import { useConfirmDialog } from '../hooks/useConfirmDialog';
/**
 * Pagina Admin - Configurazione VAT Lookup (Ricerca P.IVA)
 * Permette al super admin di gestire il servizio di lookup P.IVA
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  Settings,
  Building2,
  Key,
  BarChart3,
  RefreshCw,
  Save,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  FileText,
  TrendingUp,
  Database
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface VatConfig {
  enabled: boolean;
  api_key: string;
  default_limit: number;
  log_calls: boolean;
  cache_duration_days: number;
}

interface VatStats {
  total_calls: number;
  cached_calls: number;
  api_calls: number;
  successful_calls: number;
  success_rate: number;
}

interface Association {
  id: string;
  name: string;
  vat_lookup_config?: {
    enabled: boolean;
    monthly_limit: number;
  };
  vat_lookup_calls?: number;
}

interface LogEntry {
  vat_code: string;
  tenant_id: string;
  tenant_type: string;
  tenant_name?: string;
  found_company_name?: string;
  cached: boolean;
  success: boolean;
  error?: string;
  timestamp: string;
}

const AdminVatLookupPage: React.FC = () => {
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<VatConfig>({
    enabled: false,
    api_key: '',
    default_limit: 0,
    log_calls: true,
    cache_duration_days: 5
  });
  const [stats, setStats] = useState<VatStats | null>(null);
  const [associations, setAssociations] = useState<Association[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [editTenant, setEditTenant] = useState<Association | null>(null);
  const [tenantEnabled, setTenantEnabled] = useState(true);
  const [tenantLimit, setTenantLimit] = useState(0);
  const [testVat, setTestVat] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  // Fetch config and stats
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, logsRes] = await Promise.all([
        axios.get(`${API}/vat-lookup/admin/config`),
        axios.get(`${API}/vat-lookup/admin/logs?limit=20`)
      ]);
      
      setConfig(configRes.data.config);
      setStats(configRes.data.stats);
      setLogs(logsRes.data.logs || []);
      
      // Fetch associations separately
      try {
        const assocRes = await axios.get(`${API}/associations`);
        setAssociations(assocRes.data.associations || assocRes.data || []);
      } catch {
        // Associations endpoint may not be available
        setAssociations([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        enabled: config.enabled.toString()
      });
      if (config.api_key) params.append('api_key', config.api_key);
      if (config.default_limit !== undefined) params.append('default_limit', config.default_limit.toString());
      if (config.cache_duration_days !== undefined) params.append('cache_duration_days', config.cache_duration_days.toString());
      
      await axios.put(`${API}/vat-lookup/admin/config?${params}`);
      toast.success('Configurazione salvata');
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTenant = async () => {
    if (!editTenant) return;
    
    try {
      const params = new URLSearchParams({
        enabled: tenantEnabled.toString()
      });
      if (tenantLimit > 0) params.append('monthly_limit', tenantLimit.toString());
      
      await axios.put(`${API}/vat-lookup/admin/tenant/association/${editTenant.id}?${params}`);
      toast.success('Configurazione tenant aggiornata');
      setEditTenant(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleResetCounters = async () => {
    if (!await confirmAction({ title: 'Reset contatori', description: 'Sei sicuro di voler resettare tutti i contatori mensili?', confirmText: 'Reset' })) return;
    
    try {
      await axios.post(`${API}/vat-lookup/admin/reset-counters`);
      toast.success('Contatori resettati');
      fetchData();
    } catch (error) {
      toast.error('Errore nel reset');
    }
  };

  const handleTestLookup = async () => {
    if (!testVat) return;
    
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.get(`${API}/vat-lookup/${testVat}`);
      setTestResult(res.data);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.response?.data?.detail || 'Errore nella ricerca'
      });
    } finally {
      setTesting(false);
    }
  };

  const openTenantEdit = (assoc: Association) => {
    setEditTenant(assoc);
    setTenantEnabled(assoc.vat_lookup_config?.enabled ?? true);
    setTenantLimit(assoc.vat_lookup_config?.monthly_limit ?? 0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Search className="h-6 w-6 text-primary" />
            VAT Lookup - Ricerca P.IVA
          </h1>
          <p className="text-muted-foreground">
            Gestisci il servizio di ricerca informazioni aziendali tramite P.IVA
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Database className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_calls}</p>
                  <p className="text-sm text-muted-foreground">Chiamate totali</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.api_calls}</p>
                  <p className="text-sm text-muted-foreground">Chiamate API</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.cached_calls}</p>
                  <p className="text-sm text-muted-foreground">Da cache</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.success_rate}%</p>
                  <p className="text-sm text-muted-foreground">Successo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configurazione Globale */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurazione Globale
            </CardTitle>
            <CardDescription>
              Impostazioni del servizio VAT Lookup
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Servizio Attivo</Label>
                <p className="text-sm text-muted-foreground">
                  Abilita/disabilita il servizio globalmente
                </p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                API Key
              </Label>
              <Input
                type="password"
                value={config.api_key}
                onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                placeholder="Inserisci API Key OpenAPI.it"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Limite Default Mensile</Label>
              <Input
                type="number"
                value={config.default_limit}
                onChange={(e) => setConfig({ ...config, default_limit: parseInt(e.target.value) || 0 })}
                placeholder="0 = illimitato"
              />
              <p className="text-xs text-muted-foreground">
                0 = illimitato. Questo limite si applica ai nuovi tenant.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Durata Cache (giorni)
              </Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={config.cache_duration_days}
                onChange={(e) => setConfig({ ...config, cache_duration_days: Math.max(1, parseInt(e.target.value) || 5) })}
                placeholder="5"
              />
              <p className="text-xs text-muted-foreground">
                I risultati delle ricerche P.IVA vengono memorizzati per questo numero di giorni prima di effettuare una nuova chiamata API.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSaveConfig} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salva Configurazione
              </Button>
              <Button variant="outline" onClick={handleResetCounters}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset Contatori
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Test Lookup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Test Ricerca P.IVA
            </CardTitle>
            <CardDescription>
              Testa il servizio con una P.IVA
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={testVat}
                onChange={(e) => setTestVat(e.target.value)}
                placeholder="Es: 01234567890"
                className="flex-1"
              />
              <Button onClick={handleTestLookup} disabled={testing || !testVat}>
                {testing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            
            {testResult && (
              <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {testResult.success ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">Azienda trovata</span>
                    </div>
                    <div className="text-sm space-y-1">
                      <p><strong>Ragione Sociale:</strong> {testResult.data?.company_name}</p>
                      <p><strong>Indirizzo:</strong> {testResult.data?.address}</p>
                      <p><strong>PEC:</strong> {testResult.data?.pec || 'N/D'}</p>
                      <p><strong>Codice SDI:</strong> {testResult.data?.sdi_code || 'N/D'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700">
                    <XCircle className="h-4 w-4" />
                    <span>{testResult.error}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tenant Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Configurazione per Associazione
          </CardTitle>
          <CardDescription>
            Abilita/disabilita il servizio e imposta limiti per ogni associazione
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Associazione</TableHead>
                <TableHead className="text-center">Stato</TableHead>
                <TableHead className="text-center">Limite Mensile</TableHead>
                <TableHead className="text-center">Chiamate Usate</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {associations.map((assoc) => (
                <TableRow key={assoc.id}>
                  <TableCell className="font-medium">{assoc.name}</TableCell>
                  <TableCell className="text-center">
                    {assoc.vat_lookup_config?.enabled !== false ? (
                      <Badge variant="default" className="bg-green-500">Attivo</Badge>
                    ) : (
                      <Badge variant="secondary">Disattivo</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {assoc.vat_lookup_config?.monthly_limit || 'Illimitato'}
                  </TableCell>
                  <TableCell className="text-center">
                    {assoc.vat_lookup_calls || 0}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openTenantEdit(assoc)}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Log Recenti
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data/Ora</TableHead>
                <TableHead>P.IVA</TableHead>
                <TableHead>Azienda Trovata</TableHead>
                <TableHead>Richiesto da</TableHead>
                <TableHead className="text-center">Cache</TableHead>
                <TableHead className="text-center">Risultato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(log.timestamp).toLocaleString('it-IT')}
                  </TableCell>
                  <TableCell className="font-mono">{log.vat_code}</TableCell>
                  <TableCell className="text-sm">
                    {log.found_company_name || '-'}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="flex flex-col">
                      <span className="font-medium">{log.tenant_name || 'N/D'}</span>
                      <span className="text-xs text-muted-foreground">
                        {log.tenant_type === 'association' ? 'Associazione' : 'Azienda'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {log.cached ? (
                      <Badge variant="secondary">Cache</Badge>
                    ) : (
                      <Badge variant="outline">API</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {log.success ? (
                      <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Tenant Dialog */}
      <Dialog open={!!editTenant} onOpenChange={() => setEditTenant(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configura VAT Lookup per {editTenant?.name}</DialogTitle>
            <DialogDescription>
              Imposta le opzioni del servizio per questa associazione
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <Label>Servizio Attivo</Label>
              <Switch
                checked={tenantEnabled}
                onCheckedChange={setTenantEnabled}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Limite Mensile Chiamate</Label>
              <Input
                type="number"
                value={tenantLimit}
                onChange={(e) => setTenantLimit(parseInt(e.target.value) || 0)}
                placeholder="0 = illimitato"
              />
              <p className="text-xs text-muted-foreground">
                Chiamate usate questo mese: {editTenant?.vat_lookup_calls || 0}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTenant(null)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateTenant}>
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVatLookupPage;
